import {
  useMutation,
  useQuery as useReactQuery,
  useQueryClient,
} from "react-query";
import FetchClients, { ClientOutput } from "../client";

export type WasteContainer = ClientOutput["utilities"]["WasteContainer"];
export type WasteContainerRequest =
  ClientOutput["utilities"]["WasteContainerRequest"];
export type WasteType = ClientOutput["utilities"]["WasteType"];

export const WASTE_TYPE_LABELS: Record<WasteType, string> = {
  trash: "Trash",
  mixed_recyclables: "Recycling",
  compostable: "Compost",
};
// TODO (stevenb): Replace after migration to new unit endpoints in utility service
export const WASTE_UNIT_LABELS = {
  25: "Yd3",
  23: "Gallons",
  39: "Liters",
} as const;
export const PERCENT_FULL_LABELS: Record<
  WasteContainer["average_percent_full"],
  string
> = {
  Zero: "0%",
  TwentyFive: "25%",
  Fifty: "50%",
  SeventyFive: "75%",
  Hundred: "100%",
};
export type SupportedWasteUnitId = keyof typeof WASTE_UNIT_LABELS;
export const isWasteContainerActive = (wasteContainer: WasteContainer) =>
  !wasteContainer.valid_end_date;

const wasteContainers = {
  useQuery,
  mutations: {
    usePost,
    usePut,
    useDelete,
  },
} as const;

export default wasteContainers;

const WASTE_CONTAINERS_QUERY_KEY = "waste-containers";
function useQuery(siteId: number) {
  return useReactQuery<WasteContainer[]>({
    queryKey: [WASTE_CONTAINERS_QUERY_KEY, siteId],
    queryFn: async () => {
      const { data, error } = await FetchClients.utilities.GET(
        "/{site_id}/waste_containers",
        {
          params: {
            path: {
              site_id: siteId,
            },
          },
        }
      );
      if (data) return data;
      throw new Error(error);
    },
  });
}

function usePost(siteId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: WasteContainerRequest) => {
      const { data, error } = await FetchClients.utilities.POST(
        "/{site_id}/waste_containers",
        {
          params: {
            path: {
              site_id: siteId,
            },
          },
          body,
        }
      );
      if (data) return data;
      throw new Error(error);
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [WASTE_CONTAINERS_QUERY_KEY, siteId],
      }),
  });
}

function usePut(siteId: number, wasteContainerId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: WasteContainerRequest) => {
      const { data, error } = await FetchClients.utilities.PUT(
        "/{site_id}/waste_containers/{waste_container_id}",
        {
          params: {
            path: {
              site_id: siteId,
              waste_container_id: wasteContainerId,
            },
          },
          body,
        }
      );
      if (data) return data;
      throw new Error(error);
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [WASTE_CONTAINERS_QUERY_KEY, siteId],
      }),
  });
}

function useDelete(siteId: number, wasteContainerId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await FetchClients.utilities.DELETE(
        "/{site_id}/waste_containers/{waste_container_id}",
        {
          params: {
            path: {
              site_id: siteId,
              waste_container_id: wasteContainerId,
            },
          },
        }
      );
      if (data) return data;
      throw new Error(error);
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [WASTE_CONTAINERS_QUERY_KEY, siteId],
      }),
  });
}
